/**
 * What a team has decided about a race: the rules it runs under, when it may
 * stop, and who drives when.
 *
 * One document per team per session, at orgs/{orgId}/racePlans/{sessionId}.
 * Team-owned rather than catalogue data, because published window times
 * occasionally differ from the pattern and a race director may move them — and
 * a team correcting one mid-race should not have to be an administrator.
 *
 * Nothing here names a series. Two shapes cover everything discussed:
 *
 *   Fixed driver-change windows, anchored to the clock — a driver change is
 *   mandatory in each window, and the windows do not move if the race starts
 *   late. Every driver takes the same number of stints, give or take one.
 *
 *   Free stops with a minimum count — stop when you like, but there is a
 *   longest permitted stint, a cap on each driver's total, and no consecutive
 *   stints by the same person.
 *
 * Both are configuration. Neither is a code path.
 */

import type { Timestamp } from 'firebase/firestore'
import type { FuelPlan } from './fuelStrategy.ts'

/**
 * What a window's bounds are measured from.
 *
 * Not cosmetic: it decides what a red flag does. With `timeOfDay` the race
 * clock stops and the windows do not, so a stoppage EATS them. With `elapsed`
 * they move with the race. `remaining` is anchored to an end that can itself
 * move, so it is the least dependable of the three.
 */
export type WindowAnchor = 'timeOfDay' | 'elapsed' | 'remaining'

/**
 * One window, stored as an offset from whatever the anchor names:
 *
 *   timeOfDay — milliseconds since the epoch, so the offset IS the instant
 *   elapsed   — milliseconds after the session clock started, positive
 *   remaining — milliseconds relative to the end, so NEGATIVE
 *
 * Signed offsets rather than magnitudes, so that `opensAt < closesAt` holds
 * whatever the anchor is. A window ninety to seventy minutes before the end is
 * -90m to -70m, which sorts and validates like every other window; storing it
 * as 90 to 70 would have made the one invariant everything else relies on
 * anchor-dependent, and the resolver would have had to swap the pair back.
 */
export interface PitWindow {
  opensAt: number
  closesAt: number
  /** Free text, for a window a circuit numbers oddly. */
  label?: string
}

/** Whether a component of a stop may happen outside a window. */
export type Allowed = 'windowOnly' | 'anytime'

export interface StopComponent {
  allowed: Allowed
  /**
   * Shortest permitted duration for this component.
   *
   * Per component rather than per stop, because they genuinely differ: some
   * series set a minimum on the driver change and none on fuel, and at least
   * one sets a long minimum on fuel and none on the change.
   */
  minDurationMs?: number
  /**
   * Stops that must include this, where the rules count them. Portimão
   * wanted six fuel stops from a car that needed five.
   */
  minCount?: number
}

export interface Regime {
  windows?: {
    anchor: WindowAnchor
    entries: PitWindow[]
  }
  stint?: {
    /** Longest a single driver may be out. Only meaningful without windows —
     *  a windowed race caps stints by the schedule. */
    maxDurationMs?: number
  }
  driver?: {
    /** Cumulative driving time over the whole race. */
    maxTotalDrivingMs?: number
    /** 1 means no double stinting. */
    maxConsecutiveStints?: number
    /** Every driver takes the same number of stints, within one. */
    stintBalance?: 'equalWithinOne'
    /** Minimum time out of the car between stints. Unused by the series seen
     *  so far, and the usual companion to a total cap. */
    minRestMs?: number
  }
  stops?: {
    /** Mandatory driver-change stops, where a series counts them. */
    minCount?: number
    driverChangeRequired: 'everyStop' | 'optional'
  }
  components: {
    driverChange: StopComponent
    refuel: StopComponent
    tyres?: StopComponent
  }
}

export interface RacePlanDoc {
  /** What this race is called, in the team's own words: "Race 1". */
  name: string
  /** The event it belongs to, which is what a dashboard filters on. */
  eventId: string
  /**
   * When the race is due to start and end.
   *
   * The plan's own, not the catalogue's. A timetable is a convenience that
   * pre-fills these where one exists, and most events do not have one until
   * days beforehand — a plan that could not be made without one would be
   * unusable exactly when it was most wanted. Everything that needs a race
   * frame reads these.
   */
  startsAt: number | null
  endsAt: number | null
  /**
   * The catalogue session this plan is for, where the timetable has one.
   *
   * A convenience for pre-filling and for showing which is which, never a
   * dependency: a plan resolves against its own start and end.
   */
  sessionId?: string
  regime: Regime
  /**
   * The intended rotation, as driver ids from the organisation's own list.
   *
   * An order, not a schedule: which stint each one takes follows from the
   * order and the stops actually made. A plan that fixed drivers to specific
   * windows would be wrong the first time a safety car moved a stop.
   */
  drivers: string[]
  /** The team's fuel numbers for this race, for the fuel strategy. */
  fuel?: FuelPlan
  note?: string
  updatedAt?: Timestamp
}

// --------------------------------------------------------------- defaults

const defaultComponents = (): Regime['components'] => ({
  driverChange: { allowed: 'windowOnly' },
  refuel: { allowed: 'anytime' },
})

export const emptyRegime = (): Regime => ({
  stops: { driverChangeRequired: 'optional' },
  components: defaultComponents(),
})

// -------------------------------------------------------------- templates

export interface RegimeTemplate {
  id: string
  label: string
  /** What it is for, in the terms somebody choosing would use. */
  description: string
  regime: () => Regime
  /** A starting pattern, where the shape implies one. */
  pattern?: WindowPattern
}

/**
 * Named by shape rather than by series, so a template is picked by recognising
 * the race rather than by recognising the championship.
 */
export const regimeTemplates: RegimeTemplate[] = [
  {
    id: 'fixedWindows',
    label: 'Fixed driver-change windows',
    description:
      'A driver change is mandatory in each window. Windows are set times of day and do not move if the race starts late. Everyone takes the same number of stints, within one.',
    regime: () => ({
      windows: { anchor: 'timeOfDay', entries: [] },
      driver: { stintBalance: 'equalWithinOne' },
      stops: { driverChangeRequired: 'optional' },
      components: {
        driverChange: { allowed: 'windowOnly' },
        // Allowed everywhere in the series seen so far; whether it is worth
        // doing is a strategy question about pit loss, not a rule.
        refuel: { allowed: 'anytime' },
      },
    }),
    pattern: { firstOpensAfterMs: 30 * 60_000, openForMs: 20 * 60_000, closedForMs: 20 * 60_000 },
  },
  {
    id: 'freeStops',
    label: 'Free stops, with limits',
    description:
      'Stop whenever you like, but there is a longest permitted stint, a cap on each driver’s total driving, and no two stints in a row by the same person.',
    regime: () => ({
      stint: { maxDurationMs: 90 * 60_000 },
      driver: { maxTotalDrivingMs: 510 * 60_000, maxConsecutiveStints: 1 },
      stops: { driverChangeRequired: 'everyStop' },
      components: {
        driverChange: { allowed: 'anytime' },
        refuel: { allowed: 'anytime' },
      },
    }),
  },
]

// -------------------------------------------------------------- generation

export interface WindowPattern {
  /** How long after the span begins the first window opens. */
  firstOpensAfterMs: number
  openForMs: number
  closedForMs: number
}

/**
 * Expand a repeating pattern into explicit windows.
 *
 * The output is then owned and edited, not regenerated. Real schedules drop a
 * window, move one after a stoppage, or run the last one long, and a pattern
 * cannot express any of that — so this is a way of not typing eleven pairs of
 * times, and the list it produces is the truth. Circuits publish the actual
 * times, so what comes out here is meant to be checked against them.
 */
export function generateWindows(
  pattern: WindowPattern,
  span: { from: number; to: number },
): PitWindow[] {
  const { firstOpensAfterMs, openForMs, closedForMs } = pattern
  if (openForMs <= 0 || closedForMs < 0) return []

  const out: PitWindow[] = []
  let opens = span.from + firstOpensAfterMs
  // Bounded rather than while-true: a pattern with a tiny cycle and a long span
  // should produce a short list and a raised eyebrow, not run forever.
  for (let i = 0; i < 200 && opens < span.to; i++) {
    out.push({ opensAt: opens, closesAt: Math.min(opens + openForMs, span.to) })
    opens = opens + openForMs + closedForMs
  }
  return out
}

// --------------------------------------------------------------- resolving

export interface ResolvedWindow {
  opens: Date
  closes: Date
  label?: string
  /** Position in the schedule, one-based, as a person would refer to it. */
  index: number
}

export interface ClockFrame {
  /** When the session clock started, where one has been reported. */
  startedAt?: Date | null
  /** When it is due to end. Needed only by a `remaining` anchor. */
  endsAt?: Date | null
}

/**
 * Turn stored windows into instants.
 *
 * A window that cannot be resolved — an elapsed anchor before the clock has
 * started, say — is dropped rather than guessed at. Half a schedule is a
 * visible gap; a schedule invented from a missing start time is a confident
 * wrong answer about when to pit.
 */
export function resolveWindows(regime: Regime, frame: ClockFrame): ResolvedWindow[] {
  const windows = regime.windows
  if (!windows || windows.entries.length === 0) return []

  const base =
    windows.anchor === 'timeOfDay'
      ? 0
      : windows.anchor === 'elapsed'
        ? (frame.startedAt?.getTime() ?? null)
        : (frame.endsAt?.getTime() ?? null)

  if (base === null) return []

  return windows.entries
    .map((w, i) => ({
      opens: new Date(base + w.opensAt),
      closes: new Date(base + w.closesAt),
      label: w.label,
      index: i + 1,
    }))
    .sort((a, b) => a.opens.getTime() - b.opens.getTime())
    .map((w, i) => ({ ...w, index: i + 1 }))
}

export interface WindowState {
  /** The window currently open, if one is. */
  open: ResolvedWindow | null
  /** The next one due to open. Null once they have all been and gone. */
  next: ResolvedWindow | null
  /** How many have closed. */
  passed: number
  total: number
}

export function windowStateAt(windows: readonly ResolvedWindow[], now: Date): WindowState {
  const t = now.getTime()
  const open = windows.find((w) => w.opens.getTime() <= t && t < w.closes.getTime()) ?? null
  const next = windows.find((w) => w.opens.getTime() > t) ?? null
  return {
    open,
    next,
    passed: windows.filter((w) => w.closes.getTime() <= t).length,
    total: windows.length,
  }
}

// ------------------------------------------------------------------ parse

/**
 * Read a stored plan back, filling anything absent.
 *
 * Defaults on read, no version numbers — the same migration story the rest of
 * the model uses. A plan saved today opens unchanged after a field is added
 * tomorrow.
 */
export function parseRacePlan(raw: unknown): RacePlanDoc {
  const r = asRecord(raw)
  return {
    name: typeof r.name === 'string' && r.name ? r.name : 'Race',
    eventId: typeof r.eventId === 'string' ? r.eventId : '',
    startsAt: typeof r.startsAt === 'number' && Number.isFinite(r.startsAt) ? r.startsAt : null,
    endsAt: typeof r.endsAt === 'number' && Number.isFinite(r.endsAt) ? r.endsAt : null,
    sessionId: typeof r.sessionId === 'string' && r.sessionId ? r.sessionId : undefined,
    regime: parseRegime(r.regime),
    drivers: Array.isArray(r.drivers) ? r.drivers.filter((d): d is string => typeof d === 'string') : [],
    fuel: parseFuelPlan(r.fuel),
    note: typeof r.note === 'string' && r.note ? r.note : undefined,
  }
}

function parseFuelPlan(raw: unknown): FuelPlan | undefined {
  const r = asRecord(raw)
  const out: FuelPlan = {}
  for (const k of [
    'tankLitres',
    'reserveLitres',
    'startLitres',
    'greenRate',
    'fcyRate',
    'lapMs',
    'stopMs',
    'fuelStopMs',
  ] as const) {
    const v = r[k]
    // A reserve of nothing is a choice; anything else at zero is unset.
    if (isPositive(v) || (k === 'reserveLitres' && v === 0)) out[k] = v as number
  }
  return Object.keys(out).length > 0 ? out : undefined
}

function parseRegime(raw: unknown): Regime {
  const r = asRecord(raw)
  const out = emptyRegime()

  const windows = asRecord(r.windows)
  const anchor = windows.anchor
  if (anchor === 'timeOfDay' || anchor === 'elapsed' || anchor === 'remaining') {
    out.windows = { anchor, entries: parseEntries(windows.entries) }
  }

  const stint = asRecord(r.stint)
  if (isPositive(stint.maxDurationMs)) out.stint = { maxDurationMs: stint.maxDurationMs }

  const driver = asRecord(r.driver)
  const parsedDriver: NonNullable<Regime['driver']> = {}
  if (isPositive(driver.maxTotalDrivingMs)) parsedDriver.maxTotalDrivingMs = driver.maxTotalDrivingMs
  if (isPositive(driver.maxConsecutiveStints)) {
    parsedDriver.maxConsecutiveStints = driver.maxConsecutiveStints
  }
  if (driver.stintBalance === 'equalWithinOne') parsedDriver.stintBalance = 'equalWithinOne'
  if (isPositive(driver.minRestMs)) parsedDriver.minRestMs = driver.minRestMs
  if (Object.keys(parsedDriver).length > 0) out.driver = parsedDriver

  const stops = asRecord(r.stops)
  out.stops = {
    minCount: isPositive(stops.minCount) ? stops.minCount : undefined,
    driverChangeRequired: stops.driverChangeRequired === 'everyStop' ? 'everyStop' : 'optional',
  }

  const components = asRecord(r.components)
  out.components = {
    driverChange: parseComponent(components.driverChange, 'windowOnly'),
    refuel: parseComponent(components.refuel, 'anytime'),
  }
  if (components.tyres) out.components.tyres = parseComponent(components.tyres, 'anytime')

  return out
}

function parseComponent(raw: unknown, fallback: Allowed): StopComponent {
  const r = asRecord(raw)
  const allowed = r.allowed === 'windowOnly' || r.allowed === 'anytime' ? r.allowed : fallback
  const out: StopComponent = { allowed }
  if (isPositive(r.minDurationMs)) out.minDurationMs = r.minDurationMs
  if (isPositive(r.minCount)) out.minCount = Math.round(r.minCount)
  return out
}

function parseEntries(raw: unknown): PitWindow[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map(asRecord)
    .filter((w) => typeof w.opensAt === 'number' && typeof w.closesAt === 'number')
    .filter((w) => (w.closesAt as number) > (w.opensAt as number))
    .map((w) => ({
      opensAt: w.opensAt as number,
      closesAt: w.closesAt as number,
      label: typeof w.label === 'string' && w.label ? w.label : undefined,
    }))
    .sort((a, b) => a.opensAt - b.opensAt)
}

function isPositive(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v > 0
}

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
}

/**
 * What to write back.
 *
 * Drops `undefined`, which Firestore refuses outright, and writes only fields
 * we own.
 */
export function toStoredRacePlan(plan: RacePlanDoc): Record<string, unknown> {
  const out: Record<string, unknown> = {
    name: plan.name,
    eventId: plan.eventId,
    startsAt: plan.startsAt,
    endsAt: plan.endsAt,
    regime: prune(plan.regime) as Record<string, unknown>,
    drivers: [...plan.drivers],
  }
  if (plan.sessionId) out.sessionId = plan.sessionId
  if (plan.fuel) {
    const fuel = parseFuelPlan(plan.fuel)
    if (fuel) out.fuel = fuel
  }
  if (plan.note) out.note = plan.note
  return out
}

/**
 * Whether two plans say the same thing.
 *
 * Not a JSON comparison of the objects as they stand. A plan built in the
 * editor and the same plan read back from the store differ in key order,
 * in `undefined` fields and in window order, and a comparison that saw those
 * differences reported a plan as unsaved straight after it had been saved.
 * Both sides are put through the same write-and-read-back first, so what is
 * compared is what would be stored.
 */
export function sameRacePlan(a: RacePlanDoc, b: RacePlanDoc): boolean {
  return racePlanKey(a) === racePlanKey(b)
}

/** A plan as the string it would be stored as, for comparing. */
export function racePlanKey(plan: RacePlanDoc): string {
  return stableStringify(toStoredRacePlan(parseRacePlan(toStoredRacePlan(plan))))
}

/**
 * Why a plan's start and end cannot be right, or null where they can.
 *
 * Only the one impossible case. A plan with no times yet is unfinished rather
 * than wrong, and saving it half done is how plans get made.
 */
export function planTimesProblem(plan: Pick<RacePlanDoc, 'startsAt' | 'endsAt'>): string | null {
  if (plan.startsAt == null || plan.endsAt == null) return null
  if (plan.endsAt <= plan.startsAt) return 'The race ends before it starts.'
  return null
}

/**
 * The template a regime still is, or null once it has been changed from one.
 *
 * The windows themselves are ignored: a template says windows exist and how
 * they are measured, and filling in their times is using it, not departing
 * from it.
 */
export function templateFor(regime: Regime): RegimeTemplate | null {
  const shape = (r: Regime) =>
    stableStringify(
      prune(
        parseRegime({ ...r, windows: r.windows ? { ...r.windows, entries: [] } : undefined }),
      ),
    )
  const mine = shape(regime)
  return regimeTemplates.find((t) => shape(t.regime()) === mine) ?? null
}

/**
 * A template's rules, keeping the windows already entered where it measures
 * them the same way.
 *
 * Choosing the format after generating the schedule should not throw the
 * schedule away. Where the anchor differs the old times mean something else,
 * so they go.
 */
export function applyTemplate(current: Regime, template: RegimeTemplate): Regime {
  const next = template.regime()
  if (next.windows && current.windows?.anchor === next.windows.anchor) {
    next.windows = { ...next.windows, entries: current.windows.entries }
  }
  return next
}

function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_, v: unknown) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(
          Object.entries(v as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
        )
      : v,
  )
}

const dayMs = 24 * 60 * 60 * 1000

/**
 * The plan with a new start, its time-of-day windows moved with it by whole
 * days.
 *
 * Time-of-day windows are stored as instants, so a plan whose date was wrong
 * kept every window on the wrong day when the date was corrected — Portimao's
 * "Race 1" sat a week early with all eleven windows closed, and the editor
 * showed only "09:30" for each. Moving the start to another day now takes the
 * windows with it. Moving it by minutes does not: a time-of-day window is a
 * set time on the day, which is the point of that anchor, and a race starting
 * late does not move it.
 */
export function withStart(plan: RacePlanDoc, startsAt: number | null): RacePlanDoc {
  const windows = plan.regime.windows
  const days =
    plan.startsAt != null && startsAt != null ? Math.round((startsAt - plan.startsAt) / dayMs) : 0
  if (days === 0 || windows?.anchor !== 'timeOfDay' || windows.entries.length === 0) {
    return { ...plan, startsAt }
  }
  const shift = days * dayMs
  return {
    ...plan,
    startsAt,
    regime: {
      ...plan.regime,
      windows: {
        ...windows,
        entries: windows.entries.map((w) => ({ ...w, opensAt: w.opensAt + shift, closesAt: w.closesAt + shift })),
      },
    },
  }
}

/**
 * Which time-of-day windows fall outside the race's own start and end, by
 * index. A window an hour after the flag is a typing slip; eleven of them a
 * week early is a plan on the wrong day.
 */
export function windowsOutsideRace(plan: RacePlanDoc): number[] {
  const windows = plan.regime.windows
  if (windows?.anchor !== 'timeOfDay' || plan.startsAt == null || plan.endsAt == null) return []
  const { startsAt, endsAt } = plan
  return windows.entries.flatMap((w, i) => (w.opensAt < startsAt || w.closesAt > endsAt ? [i] : []))
}

/**
 * Why the plan's dates cannot be this event's, or null where they can.
 *
 * Only the days are compared — the event's own dates are whole days, and a
 * plan for the Sunday of a Friday-to-Sunday meeting is inside it whatever the
 * hours say.
 */
export function planOutsideEvent(
  plan: Pick<RacePlanDoc, 'startsAt'>,
  event: { startsAt: Date | null; endsAt: Date | null },
): boolean {
  if (plan.startsAt == null || !event.startsAt || !event.endsAt) return false
  const from = startOfDay(event.startsAt)
  const to = startOfDay(event.endsAt) + dayMs
  return plan.startsAt < from || plan.startsAt >= to
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

/** A blank plan for an event, before anybody has said anything about it. */
export function newRacePlan(eventId: string, name = 'Race'): RacePlanDoc {
  return { name, eventId, startsAt: null, endsAt: null, regime: emptyRegime(), drivers: [] }
}

/**
 * The frame a plan's windows resolve against.
 *
 * The plan's own times first, because they are what somebody typed for this
 * race. The stream's reported clock start is a better answer for an elapsed
 * anchor once the race is actually running, so it wins where it exists — a race
 * that started eight minutes late really did start eight minutes late.
 */
export function frameFor(plan: RacePlanDoc, reportedStart?: Date | null): ClockFrame {
  return {
    startedAt: reportedStart ?? (plan.startsAt != null ? new Date(plan.startsAt) : null),
    endsAt: plan.endsAt != null ? new Date(plan.endsAt) : null,
  }
}

/** Whether a plan covers a moment, for deciding which one is on screen. */
export function planCovers(plan: RacePlanDoc, at: Date): boolean {
  if (plan.startsAt == null) return false
  const t = at.getTime()
  return plan.startsAt <= t && (plan.endsAt == null || t <= plan.endsAt)
}

export interface PlannedStint {
  from: number
  to: number
  /** Who the rotation says takes it, or null when nobody is rostered for it. */
  driverId: string | null
}

/**
 * The shape the plan says the race has.
 *
 * One driver segment per window, meeting the next at the middle of the window
 * that separates them.
 *
 * These used to stop when a window opened and resume when it closed, leaving
 * the window as a gap, on the reasoning that a plan says a change happens
 * somewhere inside a window rather than at an instant and a join would assert
 * a precision nobody has. That was true about the precision and wrong about the
 * drawing: somebody is in the car throughout, and a row of blocks with holes in
 * it reads as nobody driving during the window. The imprecision is already on
 * screen — the window row above is drawn to its full width, directly over the
 * join — so the driver row does not have to carry it a second time.
 *
 * The midpoint is nominal and is the honest place for it: no more likely than
 * either edge, and equally wrong in both directions rather than biased early.
 *
 * The rotation cycles, so with three drivers and eight windows the fourth stint
 * is the first driver's again. That is what a rotation IS — an order, not a
 * schedule — and it is why a plan survives a stop being taken a window early.
 *
 * Empty without windows. A free-stop race has no planned shape until somebody
 * decides on one, and one invented here would be a guess drawn as a fact.
 */
export function plannedStints(
  windows: readonly ResolvedWindow[],
  rotation: readonly string[],
  from: number,
  to: number,
): PlannedStint[] {
  if (windows.length === 0 || rotation.length === 0) return []

  const middle = (w: ResolvedWindow) =>
    Math.round((w.opens.getTime() + w.closes.getTime()) / 2)

  const out: PlannedStint[] = []
  let at = from
  windows.forEach((w, i) => {
    const change = middle(w)
    out.push({ from: at, to: change, driverId: rotation[i % rotation.length] ?? null })
    at = change
  })
  out.push({ from: at, to, driverId: rotation[windows.length % rotation.length] ?? null })
  return out.filter((s) => s.to > s.from)
}

function prune(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(prune)
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) {
      if (v === undefined) continue
      out[k] = prune(v)
    }
    return out
  }
  return value
}
